import { FC, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  BinSizeEnum,
  BinTaskEnum,
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";

import {
  DriverFlowBottomButton,
  JobSiteLabel,
  PhoneCallButton,
} from "../blocks";
import { DsDataDisplay, DsDivider, DsSwitch, DsText } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import {
  useStartTaskMutation,
  useUpdateTaskMutation,
} from "../data/mutations/taskMutations";
import { useJobQuery } from "../data/queries/jobQueries";
import {
  ServiceQueryKeys,
  useServiceQuery,
} from "../data/queries/serviceQueries";
import { TaskQueryKeys, useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { getBinSize } from "../utils/bin";
import { formatCleanupType } from "../utils/strings";
import { CustomerNameText } from "./CustomerNameText";
import { DriverTaskNotesComponent } from "./DriverTaskNotesComponent";
import { TaskTimeCounter } from "./TaskTimeCounter";

type DriverTaskDetailsPropsType = JobServiceTaskBaseType & {
  isFromServiceDetailsDrawer?: boolean;
};

const DriverTaskDetails: FC<DriverTaskDetailsPropsType> = ({
  jobId,
  serviceId,
  taskId,
  isFromServiceDetailsDrawer = false,
}) => {
  const { t } = useTranslator();

  const queryClient = useQueryClient();

  const { data: jobData } = useJobQuery({ jobId });
  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });
  const [shouldNotifyClientWhenDrivingTo, setShouldNotifyClientWhenDrivingTo] =
    useState(false);
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const { mutate: startTask, isPending: isStartingTask } =
    useStartTaskMutation();

  const {
    mutate: updateTask,
    isPending: isUpdatingTask,
    variables: taskVariables,
  } = useUpdateTaskMutation();

  const { mutate: updateService, isPending: isUpdatingService } =
    useUpdateServiceMutation();

  const shouldValidateFromTask =
    taskId !== ServiceTaskTypeEnum.JUNKREMOVAL &&
    taskId !== ServiceTaskTypeEnum.PICKUPBIN;

  const isServiceStarted = shouldValidateFromTask
    ? taskData?.started
    : serviceData?.started;

  const isDrivingToSite = shouldValidateFromTask
    ? taskData?.driverFlowStatus === TaskDriverStatusEnum.DRIVING_TO_SITE
    : serviceData?.driverFlowStatus === TaskDriverStatusEnum.DRIVING_TO_SITE;

  const isCleaningArea = shouldValidateFromTask
    ? taskData?.driverFlowStatus === TaskDriverStatusEnum.CLEANING_AREA
    : serviceData?.driverFlowStatus === TaskDriverStatusEnum.CLEANING_AREA;

  if (!jobData || !taskData) {
    return null;
  }

  const contactName = jobData.site?.contact?.name;
  const contactPhone = jobData.site?.contact?.phone;

  const isJunkJob = Boolean(taskData?.cleanupType);

  const isPickUpTask = taskId === ServiceTaskTypeEnum.PICKUPBIN;
  const isExchangeTask = taskId === ServiceTaskTypeEnum.EXCHANGEBIN;
  const isDumpAndReturnTask = taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN;

  const hasJobMultipleLoads = serviceData?.junkLoads?.length;

  // This covers a scenario where the driver is performing multiple loads and returning from the dump to site
  // In that case, we don't want to show the switch jobs button
  const shouldShowSwitchJobsButton =
    isServiceStarted && isDrivingToSite && !hasJobMultipleLoads;

  const getNextDriverFlowStatus = () => {
    // If is a junk job
    if (isJunkJob) {
      if (isCleaningArea) return TaskDriverStatusEnum.FILLING_LOAD_SIZE;

      return TaskDriverStatusEnum.TAKING_PHOTO_BEFORE_CLEANUP;
    } else {
      //Is a bin job
      if (
        taskId === ServiceTaskTypeEnum.DROPBIN ||
        taskId === ServiceTaskTypeEnum.EXCHANGEBIN
      ) {
        return TaskDriverStatusEnum.ASKING_CAN_DROP_BIN;
      }

      if (taskId === ServiceTaskTypeEnum.PICKUPBIN) {
        return TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS;
      }

      if (taskId === ServiceTaskTypeEnum.WAITANDLOADBIN) {
        return TaskDriverStatusEnum.SELECTING_BIN_FOR_SERVICE;
      }

      if (taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN) {
        return TaskDriverStatusEnum.TAKING_PHOTO_BIN_CONTENTS;
      }
    }
  };

  const onButtonClick = async () => {
    if (isServiceStarted) {
      const driverFlowStatus = getNextDriverFlowStatus();

      const promisesArray = [
        updateTask({
          jobId,
          serviceId,
          taskId,
          driverFlowStatus,
        }),
      ];

      promisesArray.push(
        updateService({
          jobId,
          serviceId,
          driverFlowStatus,
        }),
      );

      return await Promise.allSettled(promisesArray);
    }

    startTask({
      jobId,
      serviceId,
      taskId,
      shouldNotifyClient: shouldNotifyClientWhenDrivingTo,
    });
  };

  const putJobBackOnList = async () => {
    updateService({
      jobId,
      serviceId,
      started: false,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      started: false,
    });

    await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
    await queryClient.invalidateQueries({ queryKey: ServiceQueryKeys.all });
  };

  const isNextLoading =
    isStartingTask ||
    ((isUpdatingTask || isUpdatingService) &&
      Boolean(taskVariables?.driverFlowStatus));

  const isPreviousLoading =
    (isUpdatingTask || isUpdatingService) &&
    Boolean(taskVariables?.started === false);

  const getBackButtonTitle = () => {
    if (isServiceStarted && !isCleaningArea) {
      return "driver_task_details.switch_jobs";
    }
  };

  const getButtonTitle = () => {
    switch (true) {
      case isServiceStarted && !isCleaningArea:
        return "driver_task_details.arrived_at_site";

      case isServiceStarted && isCleaningArea:
        return "driver_task_details.completed_junk_removal";

      default:
        return "driver_task_details.start_drive_to_job_site";
    }
  };

  const isDriverFlowDisabled =
    jobData.binTask === BinTaskEnum.DROPOFF && jobData.size === BinSizeEnum.TBD;

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex flex-grow w-full flex-col">
        {isServiceStarted && !isFromServiceDetailsDrawer && (
          <>
            <DsText variant="pageTitle">
              {!isCleaningArea
                ? t("driver_task_details.step_1_title")
                : t("driver_task_details.step_3_title")}
            </DsText>
          </>
        )}
        {isCleaningArea && (
          <TaskTimeCounter
            jobId={jobId}
            serviceId={serviceId}
            taskId={taskId}
          />
        )}
        {isServiceStarted && !isFromServiceDetailsDrawer && (
          <DsDivider className="mt-3" />
        )}
        <DsDataDisplay
          title="driver_task_details.customer"
          data={
            <CustomerNameText
              jobId={jobId}
              serviceId={serviceId}
              variant="dataDisplayValue"
              className="font-bold"
            />
          }
        />
        {(!isServiceStarted ||
          isDrivingToSite ||
          isFromServiceDetailsDrawer) && (
          <DsDataDisplay
            title="driver_task_details.job_site"
            data={<JobSiteLabel jobSiteAddress={taskData.address} />}
            dataClassName="font-bold"
          />
        )}
        <DsDataDisplay
          title="driver_task_details.job_contact"
          data={
            <PhoneCallButton
              phoneNumber={contactPhone?.toString() ?? ""}
              title={contactName!}
            />
          }
        />

        {!isServiceStarted && (
          <>
            <div className="my-4 flex items-center">
              <DsSwitch
                value={shouldNotifyClientWhenDrivingTo}
                onChange={setShouldNotifyClientWhenDrivingTo}
              />
              <DsText variant="dataDisplayValue" className="ml-4">
                {t("driver_task_details.notify_client")}
              </DsText>
            </div>
            <DsDivider />
          </>
        )}

        {isJunkJob && (
          <DsDataDisplay
            title="driver_task_details.interior_cleanup_type"
            data={formatCleanupType({
              cleanupType: taskData.cleanupType!,
              shouldShowNoTypeAsNone: true,
            })}
          />
        )}

        {!isJunkJob && !isDumpAndReturnTask && (
          <>
            <DsDataDisplay
              title="driver_task_details.size"
              data={getBinSize(taskData, "driverPanel")}
            />
            {!isPickUpTask && (
              <>
                <DsDataDisplay
                  title="driver_task_details.aggregate"
                  data={jobData.aggregate?.material}
                />
                <DsDataDisplay
                  title="driver_task_details.aggregate_amount"
                  data={
                    jobData.aggregate
                      ? `${jobData.aggregate?.yardAmount.toString()} ${t("yard")}`
                      : null
                  }
                />
              </>
            )}

            {(isPickUpTask || isExchangeTask) && (
              <DsDataDisplay
                title="driver_task_details.bin"
                data={serviceData?.connectedTask?.assignedBin?.identifier}
              />
            )}
          </>
        )}

        <DsDataDisplay
          title="notes"
          data={
            <DriverTaskNotesComponent
              jobId={jobId}
              serviceId={serviceId}
              taskId={taskId}
            />
          }
        />
      </div>
      {!isFromServiceDetailsDrawer && (
        <DriverFlowBottomButton
          isNextButtonLoading={isNextLoading}
          isPreviousButtonLoading={isPreviousLoading}
          previousButtonTitle={getBackButtonTitle()}
          shouldShowPreviousButtonIcon={false}
          onPreviousButtonClick={
            shouldShowSwitchJobsButton
              ? () => {
                  void putJobBackOnList();
                }
              : undefined
          }
          isNextButtonDisabled={isDriverFlowDisabled}
          onNextButtonClick={() => {
            void onButtonClick();
          }}
          nextButtonTitle={getButtonTitle()}
        />
      )}
    </div>
  );
};

export { DriverTaskDetails };
